import type { HtmlBlockNodeState } from "@/pages/canvas/hooks/useHtmlBlockNode";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  HtmlBlockCardSwitch,
  Spinner,
  type BlockStateSource,
  type BlockToParentMessage,
} from "@cohesive/ui-components";
import { motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type SyntheticEvent,
} from "react";
import { createPortal } from "react-dom";
import { computeLightboxBox } from "../utils/lightboxBox";

const stop = (event: SyntheticEvent) => event.stopPropagation();

/**
 * The overlay is a portal child of its canvas node, so React synthetic
 * events would bubble from it into React Flow's node wrapper — a click on
 * the scrim would select the node it just left at rest, a right-click would
 * open the node menu. Native propagation is not a concern (the portal is
 * outside the node's DOM), only React's. Keyboard events are left alone:
 * stopping them natively would starve the window Escape listener, and the
 * `nokey` class keeps React Flow's own key handling out instead.
 */
const CONTAIN_REACT_EVENTS = {
  onClick: stop,
  onDoubleClick: stop,
  onContextMenu: stop,
  onMouseDown: stop,
  onMouseUp: stop,
  onMouseMove: stop,
  onPointerDown: stop,
  onPointerUp: stop,
};

/**
 * How long after the document is displayable the reveal waits for the
 * runtime's `settled` signal (load + web fonts + a laid-out frame) before
 * showing it anyway — an older runtime never sends it, and `load` can stall
 * on a third-party fetch.
 */
const SETTLE_CAP_MS = 2000;
/** A settle this long is noticeable: show that something is coming. */
const SPINNER_AFTER_MS = 300;

/**
 * Motion, by the book for an overlay whose content is a 2×-scaled iframe:
 * only opacity and translate are animated — never scale, which would make
 * the browser re-rasterize the scaled document every frame and read as
 * jitter on text — the entrance decelerates (ease-out) over a beat, the
 * exit accelerates (ease-in) and is quicker, and the y offset is dropped for
 * users who asked for reduced motion.
 */
const ENTER_EASE = [0.22, 1, 0.36, 1] as const;
const EXIT_EASE = [0.4, 0, 1, 1] as const;
const CARD_ENTER = { duration: 0.28, ease: ENTER_EASE };
const CARD_EXIT = { duration: 0.16, ease: EXIT_EASE };
const SCRIM_ENTER = { duration: 0.2, ease: "easeOut" as const };
const SCRIM_EXIT = { duration: 0.16, ease: "easeIn" as const };

const isTextEntryTarget = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return (
    !!el &&
    (el.tagName === "INPUT" ||
      el.tagName === "TEXTAREA" ||
      el.isContentEditable === true)
  );
};

/**
 * The block, full-size, over a scrim — its only full-size surface (blocks
 * have no detail pane). A second mount of the same card: a second iframe of
 * the same document, with the pointer live throughout (there is no canvas to
 * pan in an overlay) and islands editable when allowed, through the
 * lightbox's own edit session on the block hook.
 *
 * Sized as the authored box zoomed (see computeLightboxBox); the OVERLAY
 * scrolls — the standard lightbox pattern — so the scrollbar sits at the
 * viewport edge on the scrim, never inside the card's rounded corners, and
 * the frame carries the document's full height with nothing clipped.
 *
 * Mounted through a document.body portal: nothing here lives inside the
 * React Flow viewport, so no compositing of the overlay (its fade, the
 * card's rise) can promote the viewport layer and blur every node in
 * WebKit. For the same reason the scrim is a plain translucent ground — a
 * backdrop blur would re-rasterize the whole canvas underneath per frame.
 *
 * Sequence: the scrim answers the gesture at once; the document is revealed
 * only once it is displayable AND settled (the runtime's signal that load
 * has fired, its web fonts are in and a frame has laid out — nothing left
 * that reflows text), so it never re-wraps in view. A settle that takes a
 * beat shows a spinner on the scrim rather than dead time.
 */
export function HtmlBlockLightbox({
  record,
  htmlBlock,
  baseWidth,
  baseHeight,
  blockState,
}: {
  record: FileRecord;
  htmlBlock: HtmlBlockNodeState;
  /** The node's `cohesive.state` source, shared with the card. */
  blockState?: BlockStateSource;
  /** The node's authored footprint. */
  baseWidth: number;
  baseHeight: number;
}) {
  const { lightbox, display } = htmlBlock;
  const close = lightbox.close;
  const reduceMotion = useReducedMotion() === true;

  const [displayReady, setDisplayReady] = useState(false);
  const [settled, setSettled] = useState(false);
  const contentReady = displayReady && settled;
  useEffect(() => {
    if (!displayReady || settled) return;
    const timer = window.setTimeout(() => setSettled(true), SETTLE_CAP_MS);
    return () => window.clearTimeout(timer);
  }, [displayReady, settled]);
  // The overlay measures its own content height: the node's reports were
  // taken at the NODE's width, and this frame lays out at the authored
  // width, where wrapping differs. Same capture rule as the detail pane —
  // accumulate the max while the frame sits at the base height, apply once
  // on `settled` (the cap below covers runtimes that never send it), frozen
  // after, so a 100%-height layout cannot enter a grow/re-measure loop.
  const [overlayContentHeight, setOverlayContentHeight] = useState<
    number | null
  >(null);
  const maxReportRef = useRef(0);
  const appliedRef = useRef(false);
  const capTimerRef = useRef<number | null>(null);
  // Freezing requires a measurement — a cap fired before any report must
  // leave the capture open (see BlockContent, which shares this rule): the
  // cap arms on the FIRST report, and `settled` applies immediately.
  const applyContentHeight = useCallback(() => {
    if (appliedRef.current || maxReportRef.current <= 0) return;
    appliedRef.current = true;
    setOverlayContentHeight(maxReportRef.current);
  }, []);
  useEffect(
    () => () => {
      if (capTimerRef.current !== null)
        window.clearTimeout(capTimerRef.current);
    },
    [],
  );
  const handleBlockMessage = useCallback(
    (msg: BlockToParentMessage) => {
      if (msg.type === "content-size") {
        if (!appliedRef.current && msg.height > maxReportRef.current) {
          maxReportRef.current = msg.height;
          if (capTimerRef.current === null) {
            capTimerRef.current = window.setTimeout(
              applyContentHeight,
              SETTLE_CAP_MS,
            );
          }
        }
        return;
      }
      if (msg.type === "settled") {
        setSettled(true);
        applyContentHeight();
      }
      lightbox.onBlockMessage(msg);
    },
    [applyContentHeight, lightbox],
  );
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    if (contentReady) {
      setWaiting(false);
      return;
    }
    const timer = window.setTimeout(() => setWaiting(true), SPINNER_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [contentReady]);

  // Escape with focus in the parent document; the runtime's exit-request
  // covers Escape pressed inside the frame.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || isTextEntryTarget(event.target)) return;
      close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [close]);

  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () =>
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const box = computeLightboxBox({
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
    baseWidth,
    baseHeight,
    contentHeight: overlayContentHeight,
  });
  const name = record.path?.split("/").filter(Boolean).pop() || "Block";
  const rise = reduceMotion ? 0 : 12;

  return createPortal(
    <motion.div
      className="canvas-lightbox-scroll nokey fixed inset-0 z-[60] overflow-y-auto overflow-x-hidden overscroll-contain bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label={`${name} — expanded`}
      data-block-lightbox=""
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: SCRIM_ENTER }}
      exit={{ opacity: 0, transition: SCRIM_EXIT }}
      {...CONTAIN_REACT_EVENTS}
    >
      {waiting && !contentReady && (
        <motion.div
          className="pointer-events-none fixed inset-0 flex items-center justify-center text-white/80"
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: SCRIM_ENTER }}
        >
          <Spinner size="lg" />
        </motion.div>
      )}
      {/* Scrim / click-to-close surface. Separate from the scrolling overlay
          so clicks on the overlay's own scrollbar (which target the overlay
          element) can never close the lightbox. `m-auto` on the card centers
          short content both ways and degrades to top-aligned scrolling when
          the card is taller than the viewport. */}
      <div
        className="flex min-h-full w-full cursor-pointer py-[6vh]"
        onClick={close}
      >
        <motion.div
          className="relative m-auto cursor-auto"
          style={{ width: box.width }}
          onClick={stop}
          onDoubleClick={stop}
          // Height rides along with the entrance rather than being set as a
          // style: it is normally final before the overlay opens (the node
          // mount reported it long ago), but when it lands late — images
          // decoding, a block that only just mounted — the card grows on
          // the same curve instead of snapping and re-centering.
          initial={{ opacity: 0, y: rise, height: box.height }}
          animate={
            contentReady
              ? { opacity: 1, y: 0, height: box.height, transition: CARD_ENTER }
              : {
                  opacity: 0,
                  y: rise,
                  height: box.height,
                  transition: { duration: 0 },
                }
          }
          exit={{ opacity: 0, y: reduceMotion ? 0 : 6, transition: CARD_EXIT }}
        >
          {/* The card proper: clips the document to rounded corners. Ground
              only for chromeless blocks (their authored colors assume the
              app ground behind them); opaque documents paint their own — an
              always-on ground would peek through as a light wedge at the
              clipped corners of dark documents. */}
          <div
            className={cn(
              "h-full w-full overflow-hidden rounded-xl shadow-2xl",
              display.chromeless && "bg-background",
            )}
          >
            {/* Authored-size box, zoomed visually: the card reserves the
                scaled footprint while this renders at exactly the size the
                block was designed for. Pointer coordinates map through the
                transform via the card's rect/offsetWidth conversion. */}
            <div
              style={{
                width: baseWidth,
                height: box.frameHeight,
                transform: box.scale !== 1 ? `scale(${box.scale})` : undefined,
                transformOrigin: "top left",
              }}
            >
              <HtmlBlockCardSwitch
                kind={record.display?.kind}
                url={record.url}
                path={record.path}
                updated_at={record.updated_at}
                display={display}
                theme={htmlBlock.theme}
                transformHtml={htmlBlock.transformHtml}
                isInteractive
                islandsEditable={lightbox.islandsEditable}
                contentFrozen={lightbox.contentFrozen}
                onBlockMessage={handleBlockMessage}
                onNavigate={lightbox.onNavigate}
                onAction={lightbox.onAction}
                onRuntimeReady={htmlBlock.onRuntimeReady}
                onDisplayReady={() => setDisplayReady(true)}
                blockState={blockState}
              />
            </div>
          </div>
          {/* Close, anchored to the card's top-right corner so it reads as
              the card's own control. Literal colors on purpose: it sits on
              the (always dark) scrim and the card edge in both themes, and
              the app's neutral tokens flip in dark mode. Hover inverts the
              chip — unmistakable on any ground. */}
          <button
            type="button"
            aria-label="Close"
            title="Close (Esc)"
            onClick={close}
            className={cn(
              "absolute -right-3 -top-3 flex size-9 cursor-pointer items-center justify-center rounded-full",
              "bg-white text-[#1b1b1b] shadow-[0_2px_10px_rgba(0,0,0,0.35)] ring-1 ring-black/15",
              "transition-colors duration-150 hover:bg-[#1b1b1b] hover:text-white hover:ring-white/40",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white",
            )}
          >
            <X className="size-[18px]" strokeWidth={2.25} />
          </button>
        </motion.div>
      </div>
    </motion.div>,
    document.body,
  );
}
